import React from 'react';

import { DatePicker, DatePickerProps, Flex, RefSelectProps, Select } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';

import { DATE_VALUE_FORMAT, ICustomerFilterValue, IDateRangeValue, IStatusFilterValue } from '../../data/filters';
import { useCounterpartyOrderCustomersQuery } from '../../hooks';

interface ICustomerFilterProps {
  value?: ICustomerFilterValue;
  onChange?: (value?: ICustomerFilterValue) => void;
}

export const CustomerFilterSelect = React.forwardRef<RefSelectProps, ICustomerFilterProps>(
  ({ value, onChange, ...props }, ref) => {
    const customers = useCounterpartyOrderCustomersQuery();

    return (
      <Select
        ref={ref}
        allowClear
        loading={customers.isLoading}
        value={value?.value}
        style={{ minWidth: 220 }}
        options={(customers.data || []).map((item) => ({ value: item.id, label: item.title }))}
        onChange={(nextValue) => onChange?.(nextValue ? { value: nextValue } : undefined)}
        {...props}
      />
    );
  }
);

CustomerFilterSelect.displayName = 'CustomerFilterSelect';

export const CustomerTitle: React.FC<{ id?: number }> = ({ id }) => {
  const customers = useCounterpartyOrderCustomersQuery();

  return <>{customers.data?.find((item) => item.id === id)?.title ?? id}</>;
};

export interface IStatusOption {
  value: string;
  label: string;
}

interface IStatusFilterProps {
  options: IStatusOption[];
  value?: IStatusFilterValue;
  onChange?: (value?: IStatusFilterValue) => void;
}

export const StatusFilterSelect = React.forwardRef<RefSelectProps, IStatusFilterProps>(
  ({ options, value, onChange, ...props }, ref) => (
    <Select<string[]>
      ref={ref}
      mode="multiple"
      allowClear
      showSearch={false}
      value={value?.value}
      style={{ minWidth: 240, maxWidth: 400 }}
      options={options}
      onChange={(nextValue) => onChange?.(nextValue.length ? { value: nextValue } : undefined)}
      {...props}
    />
  )
);

StatusFilterSelect.displayName = 'StatusFilterSelect';

type PickerRef = React.ElementRef<typeof DatePicker>;

interface IDateRangeFilterProps {
  value?: IDateRangeValue;
  onChange?: (value?: IDateRangeValue) => void;
  getPopupContainer?: DatePickerProps['getPopupContainer'];
}

export const DateRangeFilter = React.forwardRef<PickerRef, IDateRangeFilterProps>(
  ({ value, onChange, getPopupContainer }, ref) => {
    const ordersT = useTranslation(NSKeys.counterpartyOrders);

    const renderPicker = (bound: keyof IDateRangeValue, pickerRef?: React.ForwardedRef<PickerRef>) => (
      <DatePicker
        ref={pickerRef}
        value={value?.[bound] ? dayjs(value[bound]) : null}
        onChange={(date) => {
          const nextValue = { ...value, [bound]: date ? date.format(DATE_VALUE_FORMAT) : undefined };
          onChange?.(nextValue.from || nextValue.to ? nextValue : undefined);
        }}
        format="DD.MM.YYYY"
        getPopupContainer={getPopupContainer}
      />
    );

    return (
      <Flex gap={12}>
        <Flex align="center" gap={8}>
          {ordersT.t('filters.from')}
          {renderPicker('from', ref)}
        </Flex>
        <Flex align="center" gap={8}>
          {ordersT.t('filters.to')}
          {renderPicker('to')}
        </Flex>
      </Flex>
    );
  }
);

DateRangeFilter.displayName = 'DateRangeFilter';
